import { AttributionEvidenceType } from "@redotech/redo-attribution-common/attribution-settings";
import { MarketingChannel } from "@redotech/redo-billing-common/marketing-channel";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down_filled.svg";
import { FILTERABLE_EVIDENCE } from "@redotech/redo-marketing-common/analytics-attribution-filters";
import {
  EVIDENCE_SECTION,
  EvidenceSection,
  evidenceMeta,
  evidenceSectionMeta,
} from "@redotech/redo-merchant-app-common/attribution/evidence-settings";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { TextRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import { ChannelFilter } from "./marketing-channel-filter";

const SECTION_BY_CHANNEL: Record<MarketingChannel, EvidenceSection> = {
  [MarketingChannel.EMAIL]: EvidenceSection.EMAIL,
  [MarketingChannel.SMS]: EvidenceSection.SMS,
};

/** The evidence a channel filter leaves in play: the other channel's can't
 *  credit anything. */
export function evidenceForChannel(
  channelFilter: ChannelFilter,
): AttributionEvidenceType[] {
  return FILTERABLE_EVIDENCE.filter(
    (evidenceType) =>
      channelFilter === "all" ||
      EVIDENCE_SECTION[evidenceType] === SECTION_BY_CHANNEL[channelFilter],
  );
}

export const MarketingAttributionEventsFilter = memo(
  function MarketingAttributionEventsFilter({
    selectedEvidence,
    channelFilter,
    onChange,
  }: {
    selectedEvidence: AttributionEvidenceType[];
    channelFilter: ChannelFilter;
    onChange: (evidence: AttributionEvidenceType[]) => void;
  }) {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [dropdownAnchorRef, setDropdownAnchorRef] =
      useState<HTMLButtonElement | null>(null);

    const availableOptions = useMemo(() => {
      const meta = evidenceMeta();
      const sections = evidenceSectionMeta();
      return evidenceForChannel(channelFilter).map(
        (evidenceType): TextRedoListItem<AttributionEvidenceType> => ({
          id: evidenceType,
          type: "text",
          text: `${sections[EVIDENCE_SECTION[evidenceType]].name}: ${meta[evidenceType].name}`,
          supportingText: meta[evidenceType].description,
          value: evidenceType,
        }),
      );
    }, [channelFilter]);

    const getDisplayText = () => {
      if (selectedEvidence.length === 0) {
        return "None selected";
      }
      if (selectedEvidence.length === availableOptions.length) {
        return "All events";
      }
      if (selectedEvidence.length === 1) {
        const option = availableOptions.find(
          (opt) => opt.value === selectedEvidence[0],
        );
        return option?.text || "1 event";
      }
      return `${selectedEvidence.length} events`;
    };

    return (
      <Flex dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          Event
        </Text>
        <RedoButton
          hierarchy="secondary"
          IconTrailing={ChevronDownIcon}
          onClick={() => setDropdownOpen(!dropdownOpen)}
          ref={setDropdownAnchorRef}
          text={getDisplayText()}
        />
        <RedoMultiselectDropdown<AttributionEvidenceType>
          dropdownAnchor={dropdownAnchorRef}
          dropdownOpen={dropdownOpen}
          fitToAnchor={false}
          items={availableOptions}
          selectedItems={availableOptions.filter((option) =>
            selectedEvidence.includes(option.value),
          )}
          setDropdownOpen={setDropdownOpen}
          setSelectedItems={(items) =>
            onChange(items.map((item) => item.value))
          }
          size="xs"
        />
      </Flex>
    );
  },
);
